import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { API_BASE } from '../config';
import { playNotificationSound } from '../utils/sound';
import { unlockAudio } from '../utils/audioUnlock';
import { speakNative } from '../utils/nativeTts';
import { playAudioBuffer } from '../utils/webAudioPlayer';
import { stopAllAudio } from '../utils/audioManager';

const VoiceCommandContext = createContext(null);
export const useVoiceCommand = () => useContext(VoiceCommandContext);

function getToken() {
  return localStorage.getItem('cogency_token') || '';
}

const GLOBAL_COMMANDS = {
  en: {
    login: ['login', 'sign in', 'log in'],
    register: ['register', 'sign up', 'create account'],
    logout: ['logout', 'sign out', 'log out'],
    goToChat: ['go to chat', 'open chat', 'chat', 'start chat'],
    goToSetup: ['go to settings', 'open settings', 'settings', 'setup', 'configure'],
    goToHistory: ['go to history', 'open history', 'history', 'show history'],
    help: ['help', 'what can i say', 'commands', 'voice help'],
    enableVoice: ['enable voice', 'turn on voice', 'start voice', 'voice on'],
    disableVoice: ['disable voice', 'turn off voice', 'stop voice', 'voice off'],
  },
  fr: {
    login: ['connexion', 'se connecter', 'connecter'],
    register: ['inscription', 's\'inscrire', 'créer un compte'],
    logout: ['déconnexion', 'se déconnecter'],
    goToChat: ['aller au chat', 'ouvrir le chat', 'chat', 'discuter'],
    goToSetup: ['aller aux paramètres', 'ouvrir les paramètres', 'paramètres', 'configuration'],
    goToHistory: ['aller à l\'historique', 'ouvrir l\'historique', 'historique'],
    help: ['aide', 'que puis-je dire', 'commandes', 'aide vocale'],
    enableVoice: ['activer la voix', 'allumer la voix', 'voix on'],
    disableVoice: ['désactiver la voix', 'éteindre la voix', 'voix off'],
  },
  ar: {
    login: ['تسجيل الدخول', 'دخول', 'الدخول'],
    register: ['تسجيل', 'إنشاء حساب', 'حساب جديد'],
    logout: ['تسجيل الخروج', 'خروج'],
    goToChat: ['الذهاب إلى الدردشة', 'فتح الدردشة', 'دردشة'],
    goToSetup: ['الذهاب إلى الإعدادات', 'فتح الإعدادات', 'إعدادات'],
    goToHistory: ['الذهاب إلى التاريخ', 'فتح التاريخ', 'تاريخ'],
    help: ['مساعدة', 'ما الذي يمكنني قوله', 'أوامر'],
    enableVoice: ['تفعيل الصوت', 'تشغيل الصوت'],
    disableVoice: ['إيقاف الصوت', 'تعطيل الصوت'],
  }
};

function matchCommand(transcript, lang) {
  const t = transcript.toLowerCase().trim();
  const cmds = GLOBAL_COMMANDS[lang] || GLOBAL_COMMANDS.en;
  for (const [action, phrases] of Object.entries(cmds)) {
    if (phrases.some(p => t.includes(p))) return action;
  }
  return null;
}

export function VoiceCommandProvider({ children, lang = 'en', t, navigate, actions }) {
  const [isListening, setIsListening] = useState(false);
  const [lastCommand, setLastCommand] = useState('');
  const [voiceEnabled, setVoiceEnabled] = useState(() => localStorage.getItem('cogency_voice_commands') !== 'false');
  const [fieldTargets, setFieldTargets] = useState({});
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const holdStartRef = useRef(null);

  const audioRef = useRef(new Audio());

  const speakFeedback = useCallback(async (text) => {
    if (!voiceEnabled || !text) return;
    unlockAudio();
    // Stop any ongoing speech before giving a new voice command feedback.
    stopAllAudio();
    try {
      const res = await fetch(`${API_BASE}/api/voice/tts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({ text })
      });
      if (res.ok) {
        const blob = await res.blob();
        try {
          await playAudioBuffer(blob, {});
        } catch (playErr) {
          console.error('Audio play failed:', playErr);
          await speakNative({ text });
        }
        return;
      }
    } catch (err) {
      console.log('Server TTS unavailable:', err.message);
    }
    // Fallback: native TTS (APK plugin or Web Speech API)
    await speakNative({ text });
  }, [voiceEnabled]);

  const executeCommand = useCallback((cmd, rawTranscript) => {
    if (!cmd) return;
    setLastCommand(rawTranscript);
    playNotificationSound();

    switch (cmd) {
      case 'login':
        actions?.goToLogin?.();
        speakFeedback(t('voiceLogin') || 'Opening login');
        break;
      case 'register':
        actions?.goToRegister?.();
        speakFeedback(t('voiceRegister') || 'Opening registration');
        break;
      case 'logout':
        actions?.logout?.();
        speakFeedback(t('voiceLogout') || 'Logging out');
        break;
      case 'goToChat':
        navigate?.('main');
        speakFeedback(t('voiceGoToChat') || 'Opening chat');
        break;
      case 'goToSetup':
        navigate?.('setup');
        speakFeedback(t('voiceGoToSetup') || 'Opening settings');
        break;
      case 'goToHistory':
        navigate?.('history');
        speakFeedback(t('voiceGoToHistory') || 'Opening history');
        break;
      case 'help':
        speakFeedback(t('voiceHelpText') || 'You can say: go to chat, go to settings, add agent, ask followed by your question, type field name followed by value, submit, logout.');
        break;
      case 'enableVoice':
        setVoiceEnabled(true);
        localStorage.setItem('cogency_voice_commands', 'true');
        speakFeedback(t('voiceEnabled') || 'Voice commands enabled');
        break;
      case 'disableVoice':
        setVoiceEnabled(false);
        localStorage.setItem('cogency_voice_commands', 'false');
        speakFeedback(t('voiceDisabled') || 'Voice commands disabled');
        break;
      default:
        if (actions?.onCustomCommand) {
          actions.onCustomCommand(cmd, rawTranscript, speakFeedback);
        }
    }
  }, [actions, navigate, speakFeedback, t]);

  const processTranscript = useCallback((transcript) => {
    if (!transcript || !voiceEnabled) return;
    const raw = transcript.trim();
    if (!raw) return;

    const cmd = matchCommand(raw, lang);
    if (cmd) {
      executeCommand(cmd, raw);
      return;
    }

    // Form field filling: "type email john at example dot com"
    const typePrefixes = lang === 'fr'
      ? ['taper ', 'écrire ', 'saisir ']
      : lang === 'ar'
      ? ['اكتب ', 'أكتب ', 'ادخل ', 'أدخل ']
      : ['type ', 'write ', 'enter '];

    for (const prefix of typePrefixes) {
      if (raw.toLowerCase().startsWith(prefix)) {
        const rest = raw.slice(prefix.length).trim();
        const fieldNames = Object.keys(fieldTargets);
        for (const field of fieldNames) {
          const fieldLabel = field.toLowerCase();
          if (rest.toLowerCase().startsWith(fieldLabel + ' ') || rest.toLowerCase().startsWith(fieldLabel)) {
            const value = rest.slice(fieldLabel.length).trim();
            if (value) {
              fieldTargets[field](value);
              speakFeedback(`${t('voiceTyped') || 'Typed'} ${fieldLabel}: ${value}`);
            }
            return;
          }
        }
      }
    }

    // Submit command
    const submitWords = lang === 'fr' ? ['soumettre', 'envoyer', 'valider'] : lang === 'ar' ? ['إرسال', 'تقديم', 'أرسل'] : ['submit', 'send', 'go'];
    if (submitWords.some(w => raw.toLowerCase().includes(w))) {
      actions?.submitForm?.();
      speakFeedback(t('voiceSubmitted') || 'Submitted');
      return;
    }

    // Chat ask: "ask what is quantum computing"
    const askPrefixes = lang === 'fr'
      ? ['demande ', 'poser la question ', 'question ']
      : lang === 'ar'
      ? ['اسأل ', 'اسأل عن ', 'سؤال ']
      : ['ask ', 'question ', 'query '];
    for (const prefix of askPrefixes) {
      if (raw.toLowerCase().startsWith(prefix)) {
        const question = raw.slice(prefix.length).trim();
        if (question && actions?.askQuestion) {
          actions.askQuestion(question);
          speakFeedback(t('voiceAsking') || 'Asking: ' + question);
        }
        return;
      }
    }

    // Read commands
    if (raw.toLowerCase().includes('read') || raw.toLowerCase().includes('lis ') || raw.toLowerCase().includes('اقرأ')) {
      if (raw.toLowerCase().includes('last') || raw.toLowerCase().includes('dernier') || raw.toLowerCase().includes('الأخير')) {
        actions?.readLastMessage?.();
        speakFeedback(t('voiceReadingLast') || 'Reading last message');
      } else if (raw.toLowerCase().includes('all') || raw.toLowerCase().includes('tout') || raw.toLowerCase().includes('الكل')) {
        actions?.readAllMessages?.();
        speakFeedback(t('voiceReadingAll') || 'Reading all messages');
      }
      return;
    }

    // Stop reading
    const stopWords = lang === 'fr' ? ['arrête', 'stop', 'silence'] : lang === 'ar' ? ['توقف', 'صمت', 'كفى'] : ['stop', 'stop reading', 'silence', 'quiet'];
    if (stopWords.some(w => raw.toLowerCase().includes(w))) {
      actions?.stopSpeaking?.();
      speakFeedback(t('voiceStopped') || 'Stopped');
      return;
    }

    // Agent commands
    if (raw.toLowerCase().includes('add agent') || raw.toLowerCase().includes('ajouter agent') || raw.includes('أضف وكيل')) {
      actions?.addAgent?.();
      speakFeedback(t('voiceAgentAdded') || 'Agent added');
      return;
    }

    if (raw.toLowerCase().includes('remove agent') || raw.toLowerCase().includes('supprimer agent') || raw.includes('احذف وكيل')) {
      const num = raw.match(/\d+/)?.[0];
      if (num && actions?.removeAgent) {
        actions.removeAgent(parseInt(num) - 1);
        speakFeedback(`${t('voiceAgentRemoved') || 'Removed agent'} ${num}`);
      }
      return;
    }

    // Unrecognized
    speakFeedback(t('voiceDidNotUnderstand') || 'I did not understand. Say help for available commands.');
  }, [voiceEnabled, lang, executeCommand, fieldTargets, actions, speakFeedback, t]);

  // Process audio via server STT (Whisper) — works on any OS/browser
  const processAudio = useCallback(async (audioBlob) => {
    try {
      const reader = new FileReader();
      reader.readAsDataURL(audioBlob);
      reader.onloadend = async () => {
        const base64 = reader.result.split(',')[1];
        const res = await fetch(`${API_BASE}/api/voice/stt`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getToken()}`
          },
          body: JSON.stringify({ audio: base64 })
        });
        const data = await res.json();
        if (data.success && data.text) {
          processTranscript(data.text);
        } else {
          speakFeedback(t('voiceDidNotUnderstand') || 'I did not understand. Say help for available commands.');
        }
      };
    } catch (err) {
      console.error('STT error:', err);
      speakFeedback(t('voiceNotSupported') || 'Voice recognition not available.');
    }
  }, [processTranscript, speakFeedback, t]);

  const startListening = useCallback(async () => {
    if (isListening || !voiceEnabled) return;
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      speakFeedback(t('voiceNotSupported') || 'Microphone access not supported.');
      return;
    }
    // Stop any ongoing speech before listening to avoid feedback loops.
    stopAllAudio();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await processAudio(audioBlob);
        stream.getTracks().forEach(t => t.stop());
      };

      mediaRecorder.start();
      setIsListening(true);
    } catch (err) {
      console.error('Microphone error:', err);
      speakFeedback(t('voiceNotSupported') || 'Could not access microphone.');
    }
  }, [isListening, voiceEnabled, processAudio, speakFeedback, t]);

  const stopListening = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try { mediaRecorderRef.current.stop(); } catch {}
    }
    setIsListening(false);
  }, []);

  // Space bar hold to listen
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code === 'Space' && !['INPUT', 'TEXTAREA'].includes(e.target.tagName)) {
        e.preventDefault();
        if (!holdStartRef.current) {
          holdStartRef.current = Date.now();
          startListening();
        }
      }
    };
    const handleKeyUp = (e) => {
      if (e.code === 'Space') {
        holdStartRef.current = null;
        stopListening();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [startListening, stopListening]);

  const registerField = useCallback((name, setter) => {
    setFieldTargets(prev => ({ ...prev, [name]: setter }));
  }, []);

  const unregisterField = useCallback((name) => {
    setFieldTargets(prev => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }, []);

  const value = {
    isListening,
    lastCommand,
    voiceEnabled,
    setVoiceEnabled,
    startListening,
    stopListening,
    speakFeedback,
    registerField,
    unregisterField,
  };

  return (
    <VoiceCommandContext.Provider value={value}>
      {children}
    </VoiceCommandContext.Provider>
  );
}
